import {
  isRuleGraphPipelineKind,
  type RuleGraphCoverage,
  type RuleGraphDto,
  type RuleGraphNode,
} from "@intelligems/ig-types";

import { analyzeRuleGraph } from "./analysis";
import {
  edgeOverlayId,
  RULE_GRAPH_CART_INPUT_NODE_ID,
  type RuleGraphNodeOverlay,
  type RuleGraphOverlaysByEdgeId,
  type RuleGraphOverlaysByNodeId,
} from "./types";

/**
 * The scenario run walk: one evaluation replayed as a beat timeline over the
 * rule rows. The response carries no execution trace — coverage is a SET of
 * traversed nodes — so the order is reconstructed from the same lane model
 * the layout draws: rows in the layout's row order, cards left to right.
 * Everything here is pure; the clock lives in useRuleGraphRunWalk.
 */

export const RUN_WALK_STEP_DWELL_MS = 380;
export const RUN_WALK_RAIL_DWELL_MS = 640;
/** Duration of the active beat's one-shot settle animation on the card. */
export const RUN_WALK_LAND_MS = 400;

export type RuleGraphRunBeat =
  | { kind: "lane-label"; nodeId: string; durationMs: number }
  | { kind: "step"; nodeId: string; durationMs: number }
  | { kind: "rail"; edgeId: string; durationMs: number };

export interface RuleGraphRunWalkPlan {
  beats: RuleGraphRunBeat[];
  totalDurationMs: number;
}

export interface RuleGraphRunWalkFrame {
  /** Index into `plan.beats`, clamped to the last beat. */
  beatIndex: number;
  /** The walk has settled — hold the final frame. */
  done: boolean;
}

const labelBeat = (nodeId: string): RuleGraphRunBeat => ({
  kind: "lane-label",
  nodeId,
  durationMs: RUN_WALK_STEP_DWELL_MS,
});

const stepBeat = (nodeId: string): RuleGraphRunBeat => ({
  kind: "step",
  nodeId,
  durationMs: RUN_WALK_STEP_DWELL_MS,
});

const railBeat = (edgeId: string): RuleGraphRunBeat => ({
  kind: "rail",
  edgeId,
  durationMs: RUN_WALK_RAIL_DWELL_MS,
});

/**
 * Build the beat timeline for one evaluation, or null when the walk cannot
 * be honest: no coverage, coverage joined to a different graph revision, or
 * a graph with no proven sequential walk (a general graph or a branch with
 * a fork). Placement order alone carries no execution meaning. Null mirrors buildScenarioOverlays'
 * degrade — callers show the static overlays and skip the walk.
 *
 * Shape of the timeline, mirroring the prototype's funnel walk:
 *
 * - With a cart on the canvas (`hasCartInput`), the cart-input panel is the
 *   FIRST beat: the walk starts where the shopper did, and the follow camera
 *   pulls the cart on screen before any row lights.
 * - Lanes are visited in the layout's row order up to and INCLUDING the
 *   fired lane; lanes after it get no beats (their final overlay state —
 *   dimmed — applies from frame zero).
 * - A skipped lane collapses to its header beat: the runtime never entered
 *   it, so its cards must not light one by one.
 * - Between lanes, the fallback edge gets its own longer "rail" beat.
 * - Shared lead-in cards (steps that run once ahead of every lane) open the
 *   walk, before the first lane header.
 * - After the fired lane: shared TAIL pipeline cards, then the output node as
 *   the final beat — the "what the shopper sees" panel lands last.
 * - An empty result walks every lane and ends on the fallback terminal's
 *   header instead.
 */
export function buildRunWalkPlan({
  graph,
  coverage,
  hasCartInput = false,
}: {
  graph: RuleGraphDto;
  coverage: RuleGraphCoverage | null;
  /** Whether the canvas draws the run's cart at the head of the rows. */
  hasCartInput?: boolean;
}): RuleGraphRunWalkPlan | null {
  if (coverage === null) {
    return null;
  }
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  if (coverage.terminalNodeId && !nodesById.has(coverage.terminalNodeId)) {
    return null;
  }
  const layout = analyzeRuleGraph(graph);
  if (!layout.canAnimate) {
    return null;
  }

  const terminal = nodesById.get(coverage.terminalNodeId);
  const endsEmpty = terminal?.kind === "fallback-terminal";
  const output = graph.nodes.find((node) => node.kind === "output");
  const fallbackTerminal = graph.nodes.find(
    (node) => node.kind === "fallback-terminal"
  );
  if (!output || !fallbackTerminal) {
    return null;
  }

  const sequence = [
    ...layout.leadInNodeIds,
    ...layout.laneOrder.flatMap(
      (id) => layout.pipelineByBranchId.get(id) ?? []
    ),
    ...layout.tailNodeIds,
  ];
  const byOrder = (a: RuleGraphNode, b: RuleGraphNode): number =>
    sequence.indexOf(a.id) - sequence.indexOf(b.id);

  const beats: RuleGraphRunBeat[] = [];
  if (hasCartInput) {
    beats.push(stepBeat(RULE_GRAPH_CART_INPUT_NODE_ID));
  }

  if (layout.mode === "branchless") {
    // The zero-branch row: cards left to right, then the ending.
    const pipeline = graph.nodes
      .filter((node) => isRuleGraphPipelineKind(node.kind))
      .sort(byOrder);
    beats.push(...pipeline.map((node) => stepBeat(node.id)));
    beats.push(
      endsEmpty ? labelBeat(fallbackTerminal.id) : stepBeat(output.id)
    );
  } else {
    const { laneOrder, leadInNodeIds } = layout;
    // Shared steps the rule runs once before any row run FIRST — replaying
    // them after the fired row would narrate an order the rule never took.
    beats.push(...leadInNodeIds.map((nodeId) => stepBeat(nodeId)));
    const firedLaneIndex = coverage.firedBranchId
      ? laneOrder.indexOf(coverage.firedBranchId)
      : -1;
    // Walk stops at the fired lane; with none identified (or an empty
    // result) every lane is walked and the ending says why.
    const lastLaneIndex =
      !endsEmpty && firedLaneIndex >= 0 ? firedLaneIndex : laneOrder.length - 1;

    for (const [laneIndex, laneId] of laneOrder.entries()) {
      if (laneIndex > lastLaneIndex) {
        break;
      }
      beats.push(labelBeat(laneId));
      if (coverage.branchStates[laneId] !== "skipped") {
        const members = graph.nodes
          .filter(
            (node) =>
              node.branchId === laneId && isRuleGraphPipelineKind(node.kind)
          )
          .sort(byOrder);
        beats.push(...members.map((node) => stepBeat(node.id)));
      }
      const isLastWalkedLane = laneIndex === lastLaneIndex;
      if (!isLastWalkedLane || endsEmpty) {
        // The rail is a real fallback edge and nothing else. In a dispatch
        // shape consecutive rows are mutually exclusive alternatives, so
        // there IS no lane i → lane i+1 edge to travel; assuming one lit a
        // rail the graph never drew. A row whose fallback lands somewhere
        // other than the row the walk moves to (or the terminal it ends on)
        // gets no rail: nothing flowed along it.
        const nextStop = isLastWalkedLane
          ? fallbackTerminal.id
          : laneOrder[laneIndex + 1];
        const edge = layout.fallbackByBranchId.get(laneId);
        const to = edge?.to;
        if (edge && to === nextStop) {
          beats.push(railBeat(edgeOverlayId(edge)));
        }
      }
    }

    if (endsEmpty) {
      beats.push(labelBeat(fallbackTerminal.id));
    } else {
      const leadIn = new Set(leadInNodeIds);
      const shared = graph.nodes
        .filter(
          (node) =>
            node.branchId === undefined &&
            isRuleGraphPipelineKind(node.kind) &&
            !leadIn.has(node.id)
        )
        .sort(byOrder);
      beats.push(...shared.map((node) => stepBeat(node.id)));
      beats.push(stepBeat(output.id));
    }
  }

  if (beats.length === 0) {
    return null;
  }
  return {
    beats,
    totalDurationMs: beats.reduce((sum, beat) => sum + beat.durationMs, 0),
  };
}

/** The frame the walk should show `elapsedMs` in — clamps and holds. */
export function runWalkFrameAt(
  plan: RuleGraphRunWalkPlan,
  elapsedMs: number
): RuleGraphRunWalkFrame {
  if (elapsedMs >= plan.totalDurationMs) {
    return { beatIndex: plan.beats.length - 1, done: true };
  }
  let cumulative = 0;
  for (const [index, beat] of plan.beats.entries()) {
    cumulative += beat.durationMs;
    if (elapsedMs < cumulative) {
      return { beatIndex: index, done: false };
    }
  }
  return { beatIndex: plan.beats.length - 1, done: true };
}

/**
 * Time-slice the FINAL overlay maps onto one frame.
 *
 * - A node whose beat is still ahead shows as dimmed (the existing preview
 *   ghosting), its chips/result withheld.
 * - The node at the current beat carries its final overlay plus `active`.
 * - Past beats — and nodes with no beat at all — show their final overlay
 *   verbatim: unwalked lanes are dimmed in the final state, so they sit dim
 *   for the whole walk, exactly like the prototype's unreached rows.
 * - The shopper-sees `result` panel (riding on the output, or on the
 *   fallback terminal for an empty run) is withheld until the walk's LAST
 *   beat: the panel is the answer, and the answer lands last.
 * - The cart-input panel (the renderer's own node, not a graph node) is
 *   never dimmed — the cart is the given — and lights on its own beat; the
 *   synthetic edge out of it dims until that beat has passed.
 * - When the frame is done the final maps are returned BY REFERENCE — the
 *   walk's last frame is structurally identical to the static overlays.
 */
export function sliceRunWalkOverlays({
  graph,
  plan,
  frame,
  finalByNodeId,
  finalByEdgeId,
}: {
  graph: RuleGraphDto;
  plan: RuleGraphRunWalkPlan;
  frame: RuleGraphRunWalkFrame;
  finalByNodeId: RuleGraphOverlaysByNodeId;
  finalByEdgeId: RuleGraphOverlaysByEdgeId;
}): {
  byNodeId: RuleGraphOverlaysByNodeId;
  byEdgeId: RuleGraphOverlaysByEdgeId;
} {
  if (frame.done) {
    return { byNodeId: finalByNodeId, byEdgeId: finalByEdgeId };
  }

  const lastBeatIndex = plan.beats.length - 1;
  const nodeBeatIndex = new Map<string, number>();
  const railBeatIndex = new Map<string, number>();
  for (const [index, beat] of plan.beats.entries()) {
    if (beat.kind === "rail") {
      railBeatIndex.set(beat.edgeId, index);
    } else {
      nodeBeatIndex.set(beat.nodeId, index);
    }
  }

  const byNodeId: RuleGraphOverlaysByNodeId = {};
  const cartFinal = finalByNodeId[RULE_GRAPH_CART_INPUT_NODE_ID];
  if (cartFinal !== undefined) {
    const cartBeat = nodeBeatIndex.get(RULE_GRAPH_CART_INPUT_NODE_ID);
    byNodeId[RULE_GRAPH_CART_INPUT_NODE_ID] =
      cartBeat !== undefined && cartBeat === frame.beatIndex
        ? { ...cartFinal, active: true }
        : { ...cartFinal };
  }
  for (const node of graph.nodes) {
    const final = finalByNodeId[node.id];
    const beat = nodeBeatIndex.get(node.id);
    const withheldResult =
      (node.kind === "output" || node.kind === "fallback-terminal") &&
      frame.beatIndex < lastBeatIndex;

    let overlay: RuleGraphNodeOverlay | undefined;
    if (beat !== undefined && beat > frame.beatIndex) {
      overlay = { dimmed: true };
    } else if (beat !== undefined && beat === frame.beatIndex) {
      // The beat under the cursor lights at full strength even when its
      // final tone is dimmed (an attempted lane): attention first, ghost
      // after the walk moves on.
      overlay = { ...final, active: true };
      delete overlay.dimmed;
    } else {
      overlay = final === undefined ? undefined : { ...final };
    }
    if (overlay !== undefined && withheldResult) {
      delete overlay.result;
    }
    if (overlay !== undefined && Object.keys(overlay).length > 0) {
      byNodeId[node.id] = overlay;
    }
  }

  const byEdgeId: RuleGraphOverlaysByEdgeId = {};
  // The cart's own edge into the row it feeds, under the same rule as every
  // other edge: dim until BOTH of its ends are behind the cursor. The cart is
  // the walk's FIRST beat, so keying the line on that beat alone lit it one
  // beat in and left the brace the only bright thing on the canvas for the
  // rest of the walk — the walk has not travelled it until the row it points
  // at lights.
  const cartBeat = nodeBeatIndex.get(RULE_GRAPH_CART_INPUT_NODE_ID);
  for (const id of Object.keys(finalByEdgeId)) {
    if (!id.startsWith(`${RULE_GRAPH_CART_INPUT_NODE_ID}->`)) {
      continue;
    }
    const targetBeat = nodeBeatIndex.get(
      id.slice(RULE_GRAPH_CART_INPUT_NODE_ID.length + 2).replace(/:flow$/, "")
    );
    const arrivesAt = Math.max(cartBeat ?? 0, targetBeat ?? 0);
    byEdgeId[id] =
      arrivesAt > frame.beatIndex ? { dimmed: true } : finalByEdgeId[id]!;
  }
  for (const edge of graph.edges) {
    const id = edgeOverlayId(edge);
    const final = finalByEdgeId[id];
    const railBeat = railBeatIndex.get(id);
    if (railBeat !== undefined) {
      if (railBeat > frame.beatIndex) {
        byEdgeId[id] = { dimmed: true };
      } else if (railBeat === frame.beatIndex) {
        byEdgeId[id] = { active: true };
      } else if (final !== undefined) {
        byEdgeId[id] = final;
      }
      continue;
    }
    const fromBeat = nodeBeatIndex.get(edge.from);
    const toBeat = nodeBeatIndex.get(edge.to);
    if (
      fromBeat !== undefined &&
      toBeat !== undefined &&
      Math.max(fromBeat, toBeat) > frame.beatIndex
    ) {
      byEdgeId[id] = { dimmed: true };
    } else if (final !== undefined) {
      byEdgeId[id] = final;
    }
  }

  return { byNodeId, byEdgeId };
}

/**
 * Whether a node (flow coordinates) sits fully inside the viewport, inset by
 * `marginPx` — pure so the camera's judgment is testable without ReactFlow.
 */
export function isNodeOutsideViewport({
  nodeRect,
  viewport,
  canvasSize,
  marginPx = 24,
}: {
  nodeRect: { x: number; y: number; width: number; height: number };
  viewport: { x: number; y: number; zoom: number };
  canvasSize: { width: number; height: number };
  marginPx?: number;
}): boolean {
  const left = nodeRect.x * viewport.zoom + viewport.x;
  const top = nodeRect.y * viewport.zoom + viewport.y;
  const right = left + nodeRect.width * viewport.zoom;
  const bottom = top + nodeRect.height * viewport.zoom;
  return (
    left < marginPx ||
    top < marginPx ||
    right > canvasSize.width - marginPx ||
    bottom > canvasSize.height - marginPx
  );
}
